<template>
  <div id="app">
    <div class="container">
      <div class="patient-info">
        <div><strong>Paciente:</strong> {{ atendimento.registro.nome }}</div>
        <!-- <div><strong>Nascimento:</strong> {{ atendimento.registro }}</div>
        <div><strong>Idade:</strong> {{ atendimento.registro.idade }}</div>
        <div><strong>Sexo:</strong> {{ atendimento.registro.sexo }}</div> -->
      </div>

      <div class="controls">
        <h2 class="section-title">Selecione o tipo de procedimento</h2>
        <div class="actions">
          <button
            v-for="action in actions"
            :key="action.value"
            @click="selectAction(action.value)"
            :class="['action-btn', { active: currentAction === action.value }]"
          >
            {{ action.label }}
          </button>
        </div>

        <div v-if="showSubActions" class="sub-actions">
          <button
            v-for="subAction in subActions"
            :key="subAction.value"
            :class="{
              'sub-action-btn': true,
              active: currentSubAction === subAction.value,
            }"
            @click="selectSubAction(subAction.value)"
          >
            <i :class="subAction.icon" />
            {{ subAction.label }}
          </button>
        </div>

        <p class="instructions">
          {{ getInstructionText() }}
        </p>
      </div>

      <div class="odontogram-container">
        <!-- Arco Superior (Dentes 1-16) -->
        <div class="jaw upper-jaw">
          <div
            v-for="tooth in upperTeeth"
            :key="tooth.id"
            class="tooth"
            :class="{
              selected: bridgeStart === tooth.id || bridgeEnd === tooth.id,
              extracted: tooth.extraction,
              'bridge-tooth': tooth.bridge,
            }"
            @click="handleSectionClick(tooth.id, 'occlusal')"
          >
            <span class="tooth-number">{{ tooth.id }}</span>

            <!-- Seções do dente - agora totalmente interativas -->
            <div
              v-for="section in 5"
              :key="section"
              :class="[
                'tooth-section',
                `section-${section}`,
                {
                  fracture: tooth.fracture.includes(section),
                  restoration: tooth.restoration.includes(section),
                },
              ]"
              @click="handleSectionClick(tooth.id, section)"
            ></div>
          </div>
        </div>

        <!-- Arco Inferior (Dentes 17-32) -->
        <div class="jaw lower-jaw">
          <div
            v-for="tooth in lowerTeeth"
            :key="tooth.id"
            class="tooth"
            :class="{
              selected: bridgeStart === tooth.id || bridgeEnd === tooth.id,
              extracted: tooth.extraction,
              'bridge-tooth': tooth.bridge,
            }"
            @click="handleSectionClick(tooth.id, 'occlusal')"
          >
            <span class="tooth-number">{{ tooth.id }}</span>

            <!-- Seções do dente -->
            <div
              v-for="section in 5"
              :key="section"
              :class="[
                'tooth-section',
                `section-${section}`,
                {
                  fracture: tooth.fracture.includes(section),
                  restoration: tooth.restoration.includes(section),
                },
              ]"
              @click="handleSectionClick(tooth.id, section)"
            ></div>
          </div>
        </div>

        <!-- Linhas de ponte -->
        <div
          v-for="(bridge, index) in bridges"
          :key="index"
          class="bridge-line"
          :style="getBridgeStyle(bridge.from, bridge.to)"
        ></div>
      </div>

      <!-- Legenda -->
      <div class="legend">
        <div class="legend-item">
          <div class="legend-color legend-fracture"></div>
          <span>Fratura</span>
        </div>
        <div class="legend-item">
          <div class="legend-color legend-restoration"></div>
          <span>Restauração</span>
        </div>
        <div class="legend-item">
          <div class="legend-color legend-extraction"></div>
          <span>Extração</span>
        </div>
        <div class="legend-item">
          <div class="legend-color legend-bridge"></div>
          <span>Ponte</span>
        </div>
      </div>

      <div v-if="showStatusMessage" class="status-message" :class="statusType">
        {{ statusMessage }}
        <span class="close-btn" @click="showStatusMessage = false">
          <i class="fas fa-times" />
        </span>
      </div>

      <div class="actions-footer">
        <button class="footer-btn reset-btn" @click="resetOdontogram">
          <i class="fas fa-undo"></i> Limpar Tudo
        </button>
        <button class="footer-btn save-btn" @click="saveOdontogram">
          <i class="fas fa-save"></i> Salvar Odontograma
        </button>
      </div>

      <footer>
        Sistema Odontológico &copy; 2023 - Todos os direitos reservados
      </footer>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
export default {
  name: "OdontogramaForm",
  data() {
    return {
      currentAction: null,
      currentSubAction: "seccion",
      statusMessage: "",
      statusType: "info",
      showStatusMessage: false,
      bridgeStart: null,
      bridgeEnd: null,
      bridges: [],
      actions: [
        { label: "Fratura", value: "fracture", icon: "fas fa-bolt" },
        { label: "Restauração", value: "restoration", icon: "fas fa-tools" },
        { label: "Extrair", value: "extraction", icon: "fas fa-tooth" },
        { label: "Ponte", value: "bridge", icon: "fas fa-link" },
        { label: "Apagar", value: "erase" },
      ],
      subActions: [
        { label: "Seção", value: "seccion", icon: "fas fa-cog" },
        { label: "Dente", value: "diente", icon: "fas fa-tooth" },
      ],
      teeth: Array.from({ length: 32 }, (_, i) => ({
        id: i + 1,
        fracture: [],
        restoration: [],
        extraction: false,
        bridge: false,
      })),
      bridges: [],
    };
  },
  computed: {
    ...mapState("atendimentoLegadoProfissionalModule", {
      atendimento: (state) => state.atendimento,
    }),
    showSubActions() {
      return ["fracture", "restoration", "erase"].includes(this.currentAction);
    },
    upperTeeth() {
      return this.teeth.slice(0, 16);
    },
    lowerTeeth() {
      return this.teeth.slice(16);
    },
    formattedJson() {
      return JSON.stringify(this.getOdontogramData(), null, 2);
    },
  },
  methods: {
    selectAction(action) {
      this.currentAction = action;
      this.bridgeStart = null;
      this.bridgeEnd = null;

      // Resetar seleção de ponte ao mudar ação
      if (action !== "bridge") {
        this.bridges = this.bridges.filter((b) => b.from && b.to);
      }

      this.showStatus(
        `Modo selecionado: ${
          this.actions.find((a) => a.value === action).label
        }`,
        "success"
      );
    },

    selectSubAction(subAction) {
      this.currentSubAction = subAction;
    },

    handleSectionClick(toothId, section) {
      const toothIndex = toothId - 1;
      const tooth = this.teeth[toothIndex];

      // Não permitir ações em dentes extraídos
      if (tooth.extraction) {
        this.showStatus("Dente extraído não pode ser modificado", "error");
        return;
      }

      switch (this.currentAction) {
        case "fracture":
          this.toggleCondition(tooth, "fracture", section, "Fratura");
          break;

        case "restoration":
          this.toggleCondition(tooth, "restoration", section, "Restauração");
          break;

        case "extraction":
          this.toggleExtraction(tooth);
          break;

        case "bridge":
          this.handleBridge(toothId);
          break;

        case "erase":
          this.handleErase(tooth, toothId, section);
          break;
      }
    },

    toggleCondition(tooth, condition, section, label) {
      if (tooth[condition].includes(section)) {
        tooth[condition] = tooth[condition].filter((s) => s !== section);
        this.showStatus(`${label} removida da seção ${section}`, "success");
      } else {
        tooth[condition].push(section);
        this.showStatus(`${label} aplicada na seção ${section}`, "success");
      }
    },

    toggleExtraction(tooth) {
      tooth.extraction = !tooth.extraction;

      // Se extraído, limpar outras marcações
      if (tooth.extraction) {
        tooth.fracture = [];
        tooth.restoration = [];
        tooth.bridge = false;
        this.showStatus("Dente marcado para extração", "success");
      } else {
        this.showStatus("Extrasão cancelada", "success");
      }
    },

    handleBridge(toothId) {
      if (this.bridgeStart === null) {
        this.bridgeStart = toothId;
        this.showStatus(
          `Selecione o segundo dente para a ponte (Dente ${toothId} selecionado)`,
          "success"
        );
      } else if (this.bridgeStart === toothId) {
        this.bridgeStart = null;
        this.showStatus("Seleção de ponte cancelada", "success");
      } else {
        this.bridgeEnd = toothId;

        // Verificar se os dentes estão no mesmo arco
        const sameArch =
          (this.bridgeStart <= 16 && toothId <= 16) ||
          (this.bridgeStart > 16 && toothId > 16);

        if (!sameArch) {
          this.showStatus(
            "Pontes devem ser entre dentes do mesmo arco",
            "error"
          );
          this.bridgeStart = null;
          this.bridgeEnd = null;
          return;
        }

        // Salvar a ponte
        this.bridges.push({
          from: this.bridgeStart,
          to: this.bridgeEnd,
          timestamp: Date.now(),
        });

        // Marcar os dentes como parte de ponte
        const startIndex = this.bridgeStart - 1;
        const endIndex = this.bridgeEnd - 1;
        this.teeth[startIndex].bridge = true;
        this.teeth[endIndex].bridge = true;

        this.showStatus(
          `Ponte criada entre dente ${this.bridgeStart} e dente ${toothId}`,
          "success"
        );

        // Resetar seleção
        this.bridgeStart = null;
        this.bridgeEnd = null;
      }
    },

    handleErase(tooth, toothId, section) {
      if (this.currentSubAction === "diente") {
        // Limpar o dente inteiro
        tooth.fracture = [];
        tooth.restoration = [];
        tooth.extraction = false;
        tooth.bridge = false;

        // Remover pontes envolvendo este dente
        this.bridges = this.bridges.filter(
          (bridge) => bridge.from !== toothId && bridge.to !== toothId
        );

        this.showStatus(`Dente ${toothId} resetado`, "success");
      } else {
        // Remover a seção específica
        tooth.fracture = tooth.fracture.filter((s) => s !== section);
        tooth.restoration = tooth.restoration.filter((s) => s !== section);

        this.showStatus(
          `Seção ${section} do dente ${toothId} resetada`,
          "success"
        );
      }
    },

    getBridgeStyle(from, to) {
      // Implementação simplificada - ajuste conforme necessário
      const bridge = this.bridges.find(
        (bridge) => bridge.from === from && bridge.to === to
      );
      if (!bridge) {
        return {
          display: "none",
        };
      }
      return {
        display: "block",
        backgroundColor: "#27ae60",
        height: "4px",
        width: "100px", // Largura fixa para simplificar
      };
    },

    getOdontogramData() {
      return {
        patient: "Raquel De Souza Silva",
        birthDate: "19/01/1986",
        examDate: new Date().toLocaleDateString("pt-BR"),
        teeth: this.teeth.map((tooth) => ({
          id: tooth.id,
          conditions: {
            fracture: [...tooth.fracture],
            restoration: [...tooth.restoration],
            extraction: tooth.extraction,
            bridge: tooth.bridge,
          },
        })),
        bridges: this.bridges.map((bridge) => ({
          from: bridge.from,
          to: bridge.to,
        })),
      };
    },

    saveOdontogram() {
      const data = this.getOdontogramData();
      this.showStatus("Odontograma salvo com sucesso!", "success");
      console.log("Dados para envio:", JSON.stringify(data, null, 2));
    },

    resetOdontogram() {
      if (!confirm("Tem certeza que deseja limpar todo o odontograma?")) return;

      this.teeth = this.teeth.map((tooth) => ({
        id: tooth.id,
        fracture: [],
        restoration: [],
        extraction: false,
        bridge: false,
      }));

      this.bridges = [];
      this.bridgeStart = null;
      this.bridgeEnd = null;
      this.showStatus("Odontograma reiniciado com sucesso", "success");
    },

    showStatus(message, type) {
      this.statusMessage = message;
      this.statusClass = type;
      setTimeout(() => {
        this.statusMessage = "";
      }, 4000);
    },

    getInstructionText() {
      switch (this.currentAction) {
        case "fracture":
          return "Clique em uma seção do dente para marcar/desmarcar uma fratura";
        case "restoration":
          return "Clique em uma seção do dente para marcar/desmarcar uma restauração";
        case "extraction":
          return "Clique em qualquer parte do dente para marcar/desmarcar extração";
        case "bridge":
          if (this.bridgeStart) {
            return `Selecione o segundo dente para criar a ponte (Dente ${this.bridgeStart} selecionado)`;
          }
          return "Selecione o primeiro dente da ponte";
        case "erase":
          return this.currentSubAction === "diente"
            ? "Clique em um dente para apagar todas as marcações"
            : "Clique em uma seção do dente para apagar marcações específicas";
        default:
          return "Selecione uma ação para começar";
      }
    },
  },
  mounted() {
    // Adicionar alguns dados de exemplo para demonstração
    setTimeout(() => {
      this.teeth[3].fracture = [1, 3];
      this.teeth[14].restoration = [2, 5];
      this.teeth[20].extraction = true;
      this.bridges.push({ from: 4, to: 6 });
      this.teeth[3].bridge = true;
      this.teeth[5].bridge = true;
      this.showStatus("Odontograma carregado com dados de exemplo", "success");
    }, 1000);
  },
};
</script>

<style scoped lang="scss">
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  background: linear-gradient(135deg, #f5f7fa, #e4edf5);
  padding: 20px;
  color: #2c3e50;
  min-height: 100vh;
}

.container {
  max-width: 1000px;
  margin: 20px auto;
  background: white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  border: 1px solid #e0e6ed;
}

header {
  background: linear-gradient(135deg, #3498db, #2c3e50);
  color: white;
  padding: 25px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

header::before {
  content: "";
  position: absolute;
  top: -50px;
  left: -50px;
  width: 150px;
  height: 150px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
}

header::after {
  content: "";
  position: absolute;
  bottom: -80px;
  right: -60px;
  width: 200px;
  height: 200px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50%;
}

h1 {
  font-size: 2.2rem;
  margin-bottom: 10px;
  position: relative;
  z-index: 2;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  position: relative;
  z-index: 2;
}

.patient-info {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  background: linear-gradient(to right, #e3f2fd, #bbdefb);
  padding: 18px;
  font-size: 1rem;
  border-bottom: 1px solid #cfd8dc;
}

.patient-info div {
  margin: 8px 15px;
  padding: 8px 15px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.controls {
  padding: 25px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
}

.section-title {
  text-align: center;
  margin-bottom: 20px;
  color: #2c3e50;
  font-size: 1.4rem;
  font-weight: 600;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-bottom: 25px;
}

.action-btn {
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  background: #e0e0e0;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
  min-width: 120px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  color: #34495e;
}

.action-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.action-btn.active {
  background: #3498db;
  color: white;
  box-shadow: 0 4px 10px rgba(52, 152, 219, 0.4);
}

.sub-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  padding: 15px;
  background: #edf5ff;
  border-radius: 8px;
  margin-top: 20px;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.sub-action-btn {
  padding: 10px 20px;
  border: 1px solid #90caf9;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
}

.sub-action-btn.active {
  background: #3498db;
  color: white;
  border-color: #3498db;
}

.instructions {
  text-align: center;
  margin-top: 15px;
  color: #7f8c8d;
  font-style: italic;
}

.odontogram-container {
  padding: 30px 20px;
  position: relative;
  min-height: 550px;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="white"/><path d="M0 0 L100 100 M100 0 L0 100" stroke="%23f0f5fa" stroke-width="1"/></svg>');
  background-size: 100px;
}

.jaw {
  display: flex;
  justify-content: center;
  margin: 90px 0;
  position: relative;
}

.upper-jaw::before {
  content: "ARCO SUPERIOR";
  position: absolute;
  top: -35px;
  left: 50%;
  transform: translateX(-50%);
  font-weight: bold;
  color: #2c3e50;
  background: #ecf0f1;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 0.9rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  letter-spacing: 1px;
}

.lower-jaw::before {
  content: "ARCO INFERIOR";
  position: absolute;
  top: -35px;
  left: 50%;
  transform: translateX(-50%);
  font-weight: bold;
  color: #2c3e50;
  background: #ecf0f1;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 0.9rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  letter-spacing: 1px;
}

.tooth {
  top: 30px;
  width: 55px;
  height: 55px;
  border: 2px solid #95a5a6;
  margin: 0 7px;
  position: relative;
  background: #fefefe;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}

.tooth:hover {
  background: #e3f2fd;
  border-color: #3498db;
  transform: translateY(-5px);
}

.tooth-number {
  position: absolute;
  top: -25px;
  font-size: 0.9rem;
  color: #2c3e50;
  font-weight: 700;
  background: #ecf0f1;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tooth-section {
  position: absolute;
  width: 22px;
  height: 22px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #ddd;
  cursor: pointer;
  transition: all 0.2s;
}

.tooth-section:hover {
  transform: scale(1.1);
  z-index: 10;
  box-shadow: 0 0 8px rgba(52, 152, 219, 0.5);
}

.section-1 {
  top: 2px;
  left: 2px;
  border-radius: 4px 0 0 0;
}
.section-2 {
  top: 2px;
  right: 2px;
  border-radius: 0 4px 0 0;
}
.section-3 {
  bottom: 2px;
  right: 2px;
  border-radius: 0 0 4px 0;
}
.section-4 {
  bottom: 2px;
  left: 2px;
  border-radius: 0 0 0 4px;
}
.section-5 {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 4px;
}

.fracture {
  background: rgba(231, 76, 60, 0.7);
}
.restoration {
  background: rgba(52, 152, 219, 0.7);
}
.extraction {
  background: repeating-linear-gradient(
    45deg,
    #c0392b,
    #c0392b 3px,
    #fefefe 3px,
    #fefefe 6px
  );
}

.bridge-line {
  position: absolute;
  height: 4px;
  background: #27ae60;
  z-index: -1;
  border-radius: 3px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.action-btn {
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  background: #e0e0e0;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
  min-width: 120px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  color: #34495e;
}

.action-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.action-btn.active {
  background: #3498db;
  color: white;
  box-shadow: 0 4px 10px rgba(52, 152, 219, 0.4);
}

.actions-footer {
  display: flex;
  justify-content: center;
  gap: 25px;
  padding: 25px;
  background: #f8f9fa;
  border-top: 1px solid #eee;
}

.footer-btn {
  padding: 12px 30px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 1rem;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}

.save-btn {
  background: linear-gradient(to right, #2ecc71, #27ae60);
  color: white;
}

.save-btn:hover {
  background: linear-gradient(to right, #27ae60, #219653);
  transform: translateY(-3px);
  box-shadow: 0 5px 12px rgba(46, 204, 113, 0.4);
}

.reset-btn {
  background: linear-gradient(to right, #ecf0f1, #bdc3c7);
  color: #2c3e50;
}

.reset-btn:hover {
  background: linear-gradient(to right, #bdc3c7, #95a5a6);
  transform: translateY(-3px);
  box-shadow: 0 5px 12px rgba(189, 195, 199, 0.4);
}

.json-viewer {
  margin: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 10px;
  border: 1px solid #e0e6ed;
  max-height: 300px;
  overflow-y: auto;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.05);
}

.json-title {
  text-align: center;
  margin-bottom: 15px;
  color: #2c3e50;
  font-weight: 600;
  font-size: 1.2rem;
}

pre {
  background: #2d2d2d;
  color: #f8f8f2;
  padding: 20px;
  border-radius: 8px;
  font-size: 0.95rem;
  line-height: 1.5;
  overflow-x: auto;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

.status {
  text-align: center;
  padding: 15px;
  margin: 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.1rem;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}

.status.success {
  background: linear-gradient(to right, #d5f5e3, #2ecc71);
  color: #145a32;
  border: 1px solid #27ae60;
}

.status.error {
  background: linear-gradient(to right, #fadbd8, #e74c3c);
  color: #78281f;
  border: 1px solid #c0392b;
}

.legend {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.legend-color {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}

.legend-fracture {
  background: rgba(231, 76, 60, 0.7);
}
.legend-restoration {
  background: rgba(52, 152, 219, 0.7);
}
.legend-extraction {
  background: repeating-linear-gradient(
    45deg,
    #c0392b,
    #c0392b 3px,
    #fefefe 3px,
    #fefefe 6px
  );
}
.legend-bridge {
  background: #27ae60;
}

footer {
  text-align: center;
  padding: 20px;
  color: #7f8c8d;
  font-size: 0.9rem;
  background: #f8f9fa;
  border-top: 1px solid #e0e6ed;
}
</style>
